/**
 * Interaktive Tages-Heatmap fuer die Zeiterfassung.
 *
 * Wochen-Grid (Mo–So × N Wochen). Jeder Tag = klickbarer Button mit
 * Hintergrund-Farbe nach Soll/Ist-Differenz:
 *   - dunkelgruen ≥ +1h, hellgruen > 0
 *   - grau (off) bei Wochenende/Feiertag/Urlaub/Abwesenheit
 *   - hellrot > -1h, dunkelrot ≤ -1h
 *   - leer-grau bei neutral 0
 *
 * Klick auf einen Tag oeffnet einen Popover mit Tag-Details + Quick-Action
 * "Korrektur einreichen" (springt zu /zeiterfassung mit pre-filled Datum).
 */
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../ui/popover';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { FileEdit, ArrowRight } from 'lucide-react';

const WEEKDAYS_DE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const STATUS_BADGE = {
  Arbeitstag: { cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200', label: 'Arbeitstag' },
  Wochenende: { cls: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300', label: 'Wochenende' },
  Urlaub: { cls: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200', label: 'Urlaub' },
  Krankheit: { cls: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200', label: 'Krankheit' },
  'Kind krank': { cls: 'bg-orange-100 text-orange-800', label: 'Kind krank' },
  Elternzeit: { cls: 'bg-blue-100 text-blue-800', label: 'Elternzeit' },
  'Krank o. Schein': { cls: 'bg-rose-100 text-rose-800', label: 'Krank o. Schein' },
};

function isOffDay(status) {
  return (
    status === 'Wochenende'
    || status?.startsWith('Feiertag')
    || status === 'Urlaub'
    || status === 'Krankheit'
    || status === 'Kind krank'
    || status === 'Elternzeit'
    || status === 'Krank o. Schein'
  );
}

function dayCellClass(diff, status, hasEntry) {
  if (status?.startsWith('Feiertag')) {
    return 'bg-orange-100 text-orange-900 dark:bg-orange-900/30 dark:text-orange-200';
  }
  if (status === 'Urlaub') {
    return 'bg-green-100 text-green-900 dark:bg-green-900/30 dark:text-green-200';
  }
  if (status === 'Krankheit' || status === 'Kind krank' || status === 'Krank o. Schein') {
    return 'bg-rose-100 text-rose-900 dark:bg-rose-900/30 dark:text-rose-200';
  }
  if (status === 'Elternzeit') {
    return 'bg-blue-100 text-blue-900 dark:bg-blue-900/30 dark:text-blue-200';
  }
  if (status === 'Wochenende') {
    return 'bg-slate-50 text-slate-400 dark:bg-slate-800/40 dark:text-slate-500';
  }
  // Arbeitstag — Farbe nach diff
  if (diff >= 1) return 'bg-emerald-600 text-white';
  if (diff > 0) return 'bg-emerald-200 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100';
  if (diff === 0 && !hasEntry) return 'bg-slate-100 text-slate-500 dark:bg-slate-800/60';
  if (diff === 0) return 'bg-emerald-100 text-emerald-900';
  if (diff > -1) return 'bg-rose-200 text-rose-900 dark:bg-rose-900/40 dark:text-rose-100';
  return 'bg-rose-600 text-white';
}

function formatDE(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}

function isoWeekKey(isoDate) {
  // Gibt eine vergleichbare ISO-Wochen-ID `YYYY-Www` zurueck.
  const d = new Date(isoDate + 'T00:00:00');
  const target = new Date(d.valueOf());
  const dayNr = (d.getDay() + 6) % 7; // Mo=0..So=6
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = new Date(target.getFullYear(), 0, 4);
  const diff = target - firstThursday;
  const weekNum = 1 + Math.round(diff / (7 * 24 * 3600 * 1000));
  return `${target.getFullYear()}-W${String(weekNum).padStart(2, '0')}`;
}

export function TimeHeatmap({ rows = [], totals = null, allowCorrection = true, testIdPrefix = 'heatmap' }) {
  const navigate = useNavigate();

  const weeks = useMemo(() => {
    const grouped = new Map();
    rows.forEach((r) => {
      const key = isoWeekKey(r.date);
      if (!grouped.has(key)) grouped.set(key, new Array(7).fill(null));
      const d = new Date(r.date + 'T00:00:00');
      const idx = (d.getDay() + 6) % 7; // Mo=0..So=6
      grouped.get(key)[idx] = r;
    });
    return Array.from(grouped.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([_, days]) => days);
  }, [rows]);

  const handleCorrection = (row) => {
    navigate(`/zeiterfassung?date=${row.date}`);
  };

  if (!rows.length) {
    return (
      <div className="text-center py-8 text-muted-foreground text-sm" data-testid={`${testIdPrefix}-empty`}>
        Keine Daten für den gewählten Zeitraum.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-container`}>
      {/* Wochentag-Header */}
      <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-medium text-muted-foreground">
        {WEEKDAYS_DE.map((d) => <div key={d}>{d}</div>)}
      </div>

      {/* Grid */}
      <div className="space-y-1">
        {weeks.map((week, wi) => (
          <div key={`week-${wi}`} className="grid grid-cols-7 gap-1">
            {week.map((row, di) => {
              if (!row) {
                return (
                  <div
                    key={`empty-${wi}-${di}`}
                    className="aspect-square rounded-md bg-muted/30"
                    aria-hidden
                  />
                );
              }
              const cls = dayCellClass(row.diff_hours, row.status, !!row.clock_in);
              const off = isOffDay(row.status);
              return (
                <Popover key={row.date}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className={`aspect-square rounded-md ${cls} text-xs font-semibold flex items-center justify-center transition-transform hover:scale-105 hover:ring-2 hover:ring-primary/50 cursor-pointer relative`}
                      data-testid={`${testIdPrefix}-day-${row.date}`}
                      title={`${formatDE(row.date)} • ${row.status} • Soll ${row.target_hours}h • Ist ${row.actual_hours}h`}
                    >
                      <span>{Number(row.date.slice(-2))}</span>
                      {!off && row.diff_hours !== 0 && (
                        <span className="absolute bottom-0.5 right-1 text-[8px] opacity-80 leading-none">
                          {row.diff_hours > 0 ? '+' : ''}{row.diff_hours}
                        </span>
                      )}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-72"
                    align="center"
                    data-testid={`${testIdPrefix}-popover-${row.date}`}
                  >
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-bold text-base">{formatDE(row.date)}</div>
                          <div className="text-xs text-muted-foreground">{row.weekday}</div>
                        </div>
                        <Badge className={STATUS_BADGE[row.status]?.cls || 'bg-slate-100'} variant="secondary">
                          {row.status}
                        </Badge>
                      </div>

                      <div className="grid grid-cols-3 gap-2 text-center pt-2 border-t">
                        <div>
                          <div className="text-[10px] uppercase text-muted-foreground">Soll</div>
                          <div className="font-mono font-semibold tabular-nums">{row.target_hours} h</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase text-muted-foreground">Ist</div>
                          <div className="font-mono font-semibold tabular-nums">{row.actual_hours} h</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase text-muted-foreground">Diff</div>
                          <div className={`font-mono font-semibold tabular-nums ${
                            row.diff_hours > 0 ? 'text-emerald-600 dark:text-emerald-400'
                              : row.diff_hours < 0 ? 'text-rose-600 dark:text-rose-400' : ''
                          }`}>
                            {row.diff_hours > 0 ? '+' : ''}{row.diff_hours} h
                          </div>
                        </div>
                      </div>

                      {(row.clock_in || row.clock_out) && (
                        <div className="text-xs text-muted-foreground pt-2 border-t">
                          <span className="font-mono">{row.clock_in || '—'}</span>
                          {' – '}
                          <span className="font-mono">{row.clock_out || '—'}</span>
                          {row.break_minutes > 0 && (
                            <span className="ml-2">• Pause {row.break_minutes} Min</span>
                          )}
                        </div>
                      )}

                      {allowCorrection && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full mt-2"
                          onClick={() => handleCorrection(row)}
                          data-testid={`${testIdPrefix}-correction-btn-${row.date}`}
                        >
                          <FileEdit className="h-3 w-3 mr-2" />
                          Zur Zeiterfassung
                          <ArrowRight className="h-3 w-3 ml-auto" />
                        </Button>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              );
            })}
          </div>
        ))}
      </div>

      {/* Summary + Legende */}
      {totals && (
        <div className="flex flex-wrap gap-2 pt-2 text-[11px] text-muted-foreground" data-testid={`${testIdPrefix}-totals`}>
          <span className="px-2 py-0.5 rounded bg-muted/50">
            Soll <span className="font-semibold text-foreground">{totals.target_hours}h</span>
          </span>
          <span className="px-2 py-0.5 rounded bg-muted/50">
            Ist <span className="font-semibold text-foreground">{totals.actual_hours}h</span>
          </span>
          <span className={`px-2 py-0.5 rounded ${totals.diff_hours < 0 ? 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200' : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200'}`}>
            Diff <span className="font-semibold">{totals.diff_hours > 0 ? '+' : ''}{totals.diff_hours}h</span>
          </span>
          {totals.vacation_days > 0 && (
            <span className="px-2 py-0.5 rounded bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200">
              Urlaub <span className="font-semibold">{totals.vacation_days} T</span>
            </span>
          )}
          {totals.absence_days > 0 && (
            <span className="px-2 py-0.5 rounded bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200">
              Abwesenheit <span className="font-semibold">{totals.absence_days} T</span>
            </span>
          )}
          {totals.holiday_days > 0 && (
            <span className="px-2 py-0.5 rounded bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200">
              Feiertag <span className="font-semibold">{totals.holiday_days} T</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
}
